import { call, delay, race, select, take, takeLatest } from 'redux-saga/effects';
import { AxiosError } from 'axios';
import * as api from '@api/QCTReadings';
import { fetchTaskExecutions } from '@api/qcTaskExecutions';
import {
  ExecutionStatus,
  WorkflowExecution,
} from '@dynamika-shared/types/executions';
import { QCTVisitLabeledScore } from '@dynamika-shared/types/QCTReading';
import { actions, selectQCTReadingsState } from './QCTReadings.slice';
import {
  QCTChangeReadingLabelAction,
  QCTReadingErrorResponse,
  QCTReadingsFetchAction,
  QCTReadingsSyncAction,
  QCTRemoveSelectedReadingAction,
  QCTSendSelectedReadingAction,
} from './QCTReadings.types';
import { mergeLabeledScores, toLabeledScores } from 'src/utils/qctHelpers';
import { dispatch, Resp } from '../helpers';
import { runToast } from '../tools';

const PING_INTERVAL = 30000; // 30 seconds

/**
 * The reason the backend gave, when it gave one.
 *
 * Plain terms
 * -----------
 * The visit endpoint refuses some saves for a reason the specialist can act on
 * — "this label is not allowed for the visit", for instance. Swallowing that
 * behind "Oops, something went wrong" leaves them with nothing to go on.
 *
 * Technical
 * ---------
 * Errors come back as `application/problem+json` (`QCTReadingErrorResponse`);
 * `detail` is its human-readable member. Anything else (a network failure, a
 * 500 with no body) has no detail and falls back to the generic message.
 */
const errorDetail = (error: unknown): string | undefined =>
  ((error as AxiosError)?.response?.data as QCTReadingErrorResponse)?.detail;

export function* fetchQCTReadingsWorker(action: QCTReadingsFetchAction) {
  try {
    const data: Resp<typeof api.fetchQCTVisitData> = yield call(
      api.fetchQCTVisitData,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );

    yield dispatch(actions.fetchQCTReadingsSuccess(data));
  } catch (error) {
    yield dispatch(actions.fetchQCTReadingsFailure(error));
  }
}

export function* fetchQCTWorkstationReadingsWorker(
  action: QCTReadingsFetchAction
) {
  try {
    const data: Resp<typeof api.fetchQCTWorkstationData> = yield call(
      api.fetchQCTWorkstationData,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );

    yield dispatch(actions.fetchQCTWorkstationReadingsSuccess(data));
  } catch (error) {
    yield call(
      runToast,
      'error',
      'Could not retrieve QCT results from the workstation'
    );

    yield dispatch(actions.fetchQCTWorkstationReadingsFailure(error));
  }
}

export function* syncWorkstationWorker(action: QCTReadingsSyncAction) {
  try {
    const execution: Resp<typeof api.syncQCTWorkstationReadings> = yield call(
      api.syncQCTWorkstationReadings,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );

    yield dispatch(
      actions.getSyncStatus({
        ...action.payload,
        executionId: execution?.executionId ?? execution?.id,
      })
    );
  } catch (error) {
    /**
     * Hand the Sync button back when the query never started.
     *
     * Plain terms
     * -----------
     * Pressing Sync immediately swaps the button for a spinner, so the
     * specialist can see something is happening. If the request to start the
     * query fails, that spinner would otherwise keep turning forever and the
     * button would never come back — leaving no way to try again short of
     * reloading the page. Restoring the status puts the button back next to
     * the error message.
     *
     * Technical
     * ---------
     * `syncWorkstation` optimistically writes `IN_PROGRESS` into the slice, and
     * nothing else resets it on this path: `getSyncStatus` is never dispatched,
     * so the poller that would normally publish a settled status never runs.
     * The previous `lastSync` is carried over untouched — the failed attempt
     * produced no execution, so the last successful query is still the truth.
     */
    const { lastSync }: ReturnType<typeof selectQCTReadingsState> =
      yield select(selectQCTReadingsState);

    yield dispatch(
      actions.syncWorkstationStatus({
        lastSync,
        status: ExecutionStatus.FAILED,
      })
    );

    yield call(runToast, 'error', 'Could not start the QCT workstation query');
    console.error('Error syncing QCT workstation readings', error);
  }
}

/**
 * Why every write re-reads the visit list before composing its payload.
 *
 * Plain terms
 * -----------
 * Saving rewrites the visit's whole reading list, so it has to be built on what
 * is stored right now — not on what this browser tab loaded earlier. Otherwise
 * a second specialist working the same visit, or simply a second action taken
 * before the previous one finished, quietly deletes rows that were already
 * saved and confirmed on screen.
 *
 * Technical
 * ---------
 * `PUT qct-visit-data` REPLACES the list and there is no per-result endpoint.
 * `state.visitData` only catches up once a follow-up GET lands, so the three
 * write workers below start from `fetchQCTVisitData` and publish the document
 * the PUT returns straight into the slice — the store is never left holding a
 * list the server has already moved past.
 */
type QCTVisitWriteTarget = {
  clinicalTrialId: string;
  taskId: string;
};

/**
 * The one shape every write to the visit takes.
 *
 * Plain terms
 * -----------
 * Saving, removing and re-labelling all do the same four things: read what the
 * visit holds right now, change one part of it, save the whole list back, and
 * tell the specialist what happened. Only the middle step differs.
 *
 * Technical
 * ---------
 * Delegated with `yield*`, not `yield call(...)`: the effects are inlined into
 * the calling worker, so the sequence the `@test/saga` DSL asserts is exactly
 * the one each worker yielded before this was extracted. `rewrite` receives the
 * stored list (never `undefined`) and returns the list to PUT.
 */
function* writeVisitData(
  { clinicalTrialId, taskId }: QCTVisitWriteTarget,
  rewrite: (stored: QCTVisitLabeledScore[]) => QCTVisitLabeledScore[],
  successMessage: string,
  failureContext: string
) {
  try {
    const stored: Resp<typeof api.fetchQCTVisitData> = yield call(
      api.fetchQCTVisitData,
      clinicalTrialId,
      taskId
    );

    const values = rewrite(stored?.data || []);

    const saved: Resp<typeof api.putQCTWorkstationDataToVisit> = yield call(
      api.putQCTWorkstationDataToVisit,
      clinicalTrialId,
      taskId,
      values
    );

    yield dispatch(actions.fetchQCTReadingsSuccess(saved));

    yield call(runToast, 'success', successMessage);
  } catch (error) {
    yield call(
      runToast,
      'error',
      errorDetail(error) || 'Oops, something went wrong'
    );
    console.error(failureContext, error);
  }
}

export function* sendToReadingsWorker(action: QCTSendSelectedReadingAction) {
  const { data, ...target } = action.payload;

  // Merged, not appended: re-picking a result the visit already holds must
  // move its label, not add the same scan a second time.
  yield* writeVisitData(
    target,
    stored => mergeLabeledScores(stored, toLabeledScores(data)),
    'Values have been saved for reading',
    'Error sending QCT results to readings'
  );
}

/**
 * Take one result back out of the reading list.
 *
 * Plain terms
 * -----------
 * The spine and the hip scan of one exam are two separate results that both
 * arrived under the same workstation record. Removing one must leave the other
 * in place.
 *
 * Technical
 * ---------
 * Matched on `scoreIndex` — the row's position in `QCTVisitDataDTO.data`, put
 * there by `toVisitReadings` — because `referenceId` is the RECORD id and two
 * scans share it, so filtering on it dropped both at once. A row that carries
 * no `scoreIndex` never came from the visit and matches nothing.
 */
export function* removeFromReadingsWorker(
  action: QCTRemoveSelectedReadingAction
) {
  const { data, ...target } = action.payload;

  yield* writeVisitData(
    target,
    stored => stored.filter((_, index) => index !== data.scoreIndex),
    'Item has been removed from readings',
    'Error removing QCT result from readings'
  );
}

/**
 * Re-label or un-label a result already selected for reading (D7-2775).
 *
 * Plain terms
 * -----------
 * The specialist picks a different label for a result they already saved, or
 * clears the label off it. The result itself stays in the reading list and its
 * measured values are untouched — only which label describes it changes.
 *
 * Technical
 * ---------
 * Three things this worker is deliberate about:
 *
 * 1. It maps over the stored scores, NOT over the row the table handed back.
 *    Rows on screen carry a `labelName` added for display by `toVisitReadings`;
 *    writing those back would post a field the visit endpoint never sent.
 * 2. The whole selection is written back, because `putQCTWorkstationDataToVisit`
 *    REPLACES the visit's result list — there is no per-result endpoint.
 * 3. Nothing is asked to confirm. The acceptance criteria treat assignment and
 *    removal symmetrically, assignment confirms nothing, and the result stays
 *    in the reading list either way — unlike `removeFromReadingsWorker`, which
 *    drops it and therefore does confirm.
 *
 * The result is found by `scoreIndex`, its position in the stored list, for the
 * same reason `removeFromReadingsWorker` uses it: two scans of one record share
 * a `referenceId`, so matching on that re-labelled both.
 */
export function* changeReadingLabelWorker(
  action: QCTChangeReadingLabelAction
) {
  const { data, labelId, ...target } = action.payload;

  yield* writeVisitData(
    target,
    stored =>
      stored.map((score, index) =>
        index === data.scoreIndex ? { ...score, labelId } : score
      ),
    labelId ? 'Label has been updated' : 'Label has been removed',
    'Error changing the label of a QCT result'
  );
}

/**
 * Poll the execution list until the workstation query settles.
 *
 * Plain terms
 * -----------
 * Asking the QCT workstation for results is not instant. This keeps checking
 * every 30 seconds and, the moment the query finishes, pulls in the results.
 *
 * Technical
 * ---------
 * `GET /qc/tasks/:taskId/executions` returns EVERY execution on the task, so it
 * is filtered to `name === 'QCT_QUERY'` — without that, an unrelated DICOM
 * upload would be read as a finished workstation query. `name` is what splits a
 * query from a "Send to QCT" export: both record `ExecutionType.QCT`, so
 * filtering on the type alone let an export move "Last Sync" while having
 * fetched nothing.
 *
 * `takeLatest` guarantees only one poller runs: a second `getSyncStatus`
 * cancels the first. The loop exits as soon as no execution is running, so it
 * does not spin forever on a task whose query already finished.
 */
export function* getSyncStatusWorker(action: QCTReadingsSyncAction) {
  const { executionId } = action.payload;

  const isTracked = (execution: WorkflowExecution) =>
    !!executionId &&
    (execution.executionId === executionId || execution.id === executionId);

  const isRunning = (execution: WorkflowExecution) =>
    execution.status === ExecutionStatus.IN_PROGRESS &&
    (!!execution.started || isTracked(execution));

  while (true) {
    try {
      const executions: Resp<typeof fetchTaskExecutions> = yield call(
        fetchTaskExecutions,
        action.payload.clinicalTrialId,
        action.payload.taskId
      );

      const statusResult = executions.filter(
        execution => execution.name === 'QCT_QUERY'
      );

      /**
       * Which execution the page is reporting on.
       *
       * Plain terms
       * -----------
       * The query this Sync started is the one the specialist is waiting for,
       * even before the backend records when it began. Only when this session
       * started nothing does the page fall back to the most recent query.
       *
       * Technical
       * ---------
       * Sorting by `started` puts an execution with `started: null` LAST, so
       * without this the tracked run loses to the previous, completed one —
       * `last.status` then reads `COMPLETED` and the worker re-fetches the
       * workstation results on every 30s tick while the query is still going.
       */
      const tracked = statusResult.find(isTracked);

      const newest = [...statusResult].sort(
        (a, b) => new Date(b.started).getTime() - new Date(a.started).getTime()
      )[0];

      const last = tracked ?? newest;

      const isLastStatusInProgress = statusResult.some(isRunning);

      if (last) {
        yield dispatch(
          actions.syncWorkstationStatus({
            lastSync: last.finished,
            status: isLastStatusInProgress
              ? ExecutionStatus.IN_PROGRESS
              : last.status,
          })
        );
      } else {
        /**
         * Nothing has ever queried the workstation for this task.
         *
         * Plain terms
         * -----------
         * Pressing Sync puts the page into "Synchronizing" straight away, so
         * the specialist sees something happen. If the task then turns out to
         * hold no workstation query at all, the page has to be handed back —
         * otherwise it spins for good with the Sync button hidden, and only a
         * reload recovers it.
         *
         * Technical
         * ---------
         * The `syncWorkstation` reducer sets `syncStatus: IN_PROGRESS`
         * optimistically. `COMPLETED` is the slice's own initial value, i.e.
         * idle, so restoring it here shows "No Data" with Sync available.
         * `lastSync` is carried over untouched: no execution means nothing new
         * to report about the previous one.
         */
        const { lastSync }: ReturnType<typeof selectQCTReadingsState> =
          yield select(selectQCTReadingsState);

        yield dispatch(
          actions.syncWorkstationStatus({
            lastSync,
            status: ExecutionStatus.COMPLETED,
          })
        );
      }

      if (last?.status === ExecutionStatus.COMPLETED) {
        yield dispatch(actions.fetchQCTWorkstationReadings(action.payload));
      }

      if (!isLastStatusInProgress) {
        break;
      }

      yield delay(PING_INTERVAL);
    } catch (error) {
      const { lastSync }: ReturnType<typeof selectQCTReadingsState> =
        yield select(selectQCTReadingsState);

      yield dispatch(
        actions.syncWorkstationStatus({
          lastSync,
          status: ExecutionStatus.FAILED,
        })
      );

      yield call(
        runToast,
        'error',
        'Could not check the QCT workstation query status'
      );

      console.error('Error polling QCT workstation sync status', error);
      break;
    }
  }
}

/**
 * Stops the poller when the specialist leaves the QCT page.
 *
 * Plain terms
 * -----------
 * The page checks the workstation query every 30 seconds. Once the specialist
 * has navigated away there is nothing left to report to, and an error raised
 * at that point would pop a toast over whatever page they moved on to.
 *
 * Technical
 * ---------
 * `takeLatest` already cancels one poll loop when another `getSyncStatus`
 * arrives; it cannot know about unmount. `QCTPage` dispatches
 * `stopSyncPolling` from its cleanup, and this race cancels `poll` — a
 * `takeLatest` worker is a forked task, so cancelling it also unwinds the
 * pending `delay`.
 */
export function* watchSyncStatusWorker(action: QCTReadingsSyncAction) {
  yield race({
    poll: call(getSyncStatusWorker, action),
    stop: take(actions.stopSyncPolling.type),
  });
}

export function* QCTReadingsSaga() {
  yield takeLatest(actions.fetchQCTReadings.type, fetchQCTReadingsWorker);
  yield takeLatest(
    actions.fetchQCTWorkstationReadings.type,
    fetchQCTWorkstationReadingsWorker
  );

  yield takeLatest(actions.syncWorkstation.type, syncWorkstationWorker);
  yield takeLatest(actions.getSyncStatus.type, watchSyncStatusWorker);
  yield takeLatest(actions.sendToReadings.type, sendToReadingsWorker);
  yield takeLatest(actions.removeSelectedReading.type, removeFromReadingsWorker);
  yield takeLatest(actions.changeReadingLabel.type, changeReadingLabelWorker);
}
